import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert, Drawer, List, ListItemButton, ListItemIcon, ListItemText, AppBar, Toolbar, IconButton, Avatar, Divider, Chip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MenuIcon from '@mui/icons-material/Menu';
import GroupIcon from '@mui/icons-material/Group';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import axios from 'axios';
import ProfileDialog from './ProfileDialog';
import { API_BASE } from './utils/apiBase';
import {
  filterLetters,
  filterDigits,
  filterPhone,
  lettersKeyDown,
  digitsKeyDown,
  phoneKeyDown,
} from './utils/inputFilters';

const ADMIN_DRAWER_WIDTH = 260;

const govDigits = (value) => String(value || '').replace(/\D/g, '');
const formatSssNumber = (value) => {
  const digits = govDigits(value).slice(0, 10);
  if (digits.length <= 2) return digits;
  if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 9)}-${digits.slice(9)}`;
};
const formatGov12Number = (value) => {
  const digits = govDigits(value).slice(0, 12);
  if (digits.length <= 4) return digits;
  if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
};
const govError = (field, value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'n/a' || raw === 'na' || raw === 'none') return '';
  const digits = govDigits(value);
  if (field === 'sss_no') return digits.length === 10 ? '' : 'SSS must be exactly 10 digits';
  return digits.length === 12 ? '' : `${field === 'philhealth_no' ? 'PhilHealth' : 'Pag-IBIG'} must be exactly 12 digits`;
};
const normalizeGovForPayload = (field, value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'n/a' || raw === 'na' || raw === 'none') return null;
  return field === 'sss_no' ? formatSssNumber(value) : formatGov12Number(value);
};

export default function AccountsPage({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [hrLeaveRequests, setHrLeaveRequests] = useState([]);
  const [salaryChanges, setSalaryChanges] = useState([]);
  const [payrollAuditRows, setPayrollAuditRows] = useState([]);
  const now = new Date();
  const [payrollAuditMonth, setPayrollAuditMonth] = useState(now.getMonth() + 1);
  const [payrollAuditYear, setPayrollAuditYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [editing, setEditing] = useState(null); // employee being edited
  const [tempDialogOpen, setTempDialogOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [tempPasswordFor, setTempPasswordFor] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [detailDeployment, setDetailDeployment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const normalizedRole = (user?.role || '').toLowerCase();
  const isManager = normalizedRole === 'hr' || normalizedRole === 'admin';
  const isAdmin = normalizedRole === 'admin';
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleOpenEdit = async (employee) => {
    setEditing({ ...employee });
    setDetailPhotos([]);
    setDetailDeployment(null);
    setDetailLoading(true);
    try {
      const [res, depRes] = await Promise.all([
        axios.get(`${API_BASE}/api/employees/${employee.id}`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/deployments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const details = res?.data || {};
      const depRows = Array.isArray(depRes?.data?.rows) ? depRes.data.rows : (Array.isArray(depRes?.data) ? depRes.data : []);
      const mine = depRows.filter((d) => String(d.user_id || d.employee_id || '') === String(employee.id));
      const active = mine.find((d) => !d.end_date) || null;
      const latest = mine.sort((a, b) => {
        const ad = new Date(a.start_date || a.created_at || 0).getTime();
        const bd = new Date(b.start_date || b.created_at || 0).getTime();
        return bd - ad;
      })[0] || null;
      setDetailDeployment(active || latest || null);
      setEditing((prev) => ({
        ...(prev || {}),
        full_name: details.full_name || prev?.full_name || '',
        sss_no: formatSssNumber(details.sss_no ?? prev?.sss_no ?? ''),
        philhealth_no: formatGov12Number(details.philhealth_no ?? prev?.philhealth_no ?? ''),
        pagibig_no: formatGov12Number(details.pagibig_no ?? prev?.pagibig_no ?? ''),
        base_salary: details.base_salary ?? prev?.base_salary ?? '',
        contract_end_date: details.contract_end_date ?? prev?.contract_end_date ?? '',
      }));
      if (String(employee.role || '').toLowerCase() === 'custodian') {
        const p = await axios.get(`${API_BASE}/api/photos_blob/list/${employee.id}`, { withCredentials: true });
        const rows = Array.isArray(p?.data?.photos) ? p.data.photos : (Array.isArray(p?.data) ? p.data : []);
        setDetailPhotos(rows || []);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load full employee details.', severity: 'warning' });
    } finally {
      setDetailLoading(false);
    }
  };
  const handleCloseEdit = () => {
    setEditing(null);
    setDetailPhotos([]);
    setDetailDeployment(null);
    setDetailLoading(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'firstname' || name === 'lastname') {
      nextValue = filterLetters(value);
    } else if (name === 'phone') {
      nextValue = filterPhone(value);
    } else if (name === 'sss_no') {
      nextValue = formatSssNumber(value);
    } else if (name === 'philhealth_no' || name === 'pagibig_no') {
      nextValue = formatGov12Number(value);
    } else if (name === 'employeeid') {
      nextValue = filterDigits(value);
    }
    setEditing(prev => ({ ...prev, [name]: nextValue }));
  };

  const handleBirthdateChange = (date) => {
    const iso = date ? new Date(date).toISOString().split('T')[0] : '';
    let age = '';
    if (date && !isNaN(new Date(date).getTime())) {
      const d = new Date(date);
      const today = new Date();
      let years = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years--;
      age = String(years >= 0 ? years : '');
    }
    setEditing(prev => ({ ...prev, birthdate: iso, age }));
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      const idErrors = [
        govError('sss_no', editing.sss_no),
        govError('philhealth_no', editing.philhealth_no),
        govError('pagibig_no', editing.pagibig_no),
      ].filter(Boolean);
      if (idErrors.length > 0) {
        setSnackbar({ open: true, message: idErrors[0], severity: 'error' });
        return;
      }
      const payload = {};
  ['firstname','middlename','lastname','email','phone','address','employeeid','role','birthdate','age','sss_no','philhealth_no','pagibig_no','salaryType','base_salary','date_hired','contract_end_date','password'].forEach(k => {
        if (Object.prototype.hasOwnProperty.call(editing, k) && typeof editing[k] !== 'undefined' && editing[k] !== null) {
          // send empty password only if explicitly set
          if (k === 'password' && editing[k] === '') return;
          payload[k === 'base_salary' ? 'baseSalary' : k] = editing[k];
        }
      });
      // normalize base salary key back to base_salary expected by server if present
      if (payload.baseSalary !== undefined) {
        payload.baseSalary = payload.baseSalary === '' ? null : Number(payload.baseSalary);
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'sss_no')) payload.sss_no = normalizeGovForPayload('sss_no', payload.sss_no);
      if (Object.prototype.hasOwnProperty.call(payload, 'philhealth_no')) payload.philhealth_no = normalizeGovForPayload('philhealth_no', payload.philhealth_no);
      if (Object.prototype.hasOwnProperty.call(payload, 'pagibig_no')) payload.pagibig_no = normalizeGovForPayload('pagibig_no', payload.pagibig_no);
      await axios.put(`${API_BASE}/api/employees/${editing.id}`, payload, { withCredentials: true });
      setEditing(null);
      fetchEmployees();
      setSnackbar({ open: true, message: 'Employee saved', severity: 'success' });
    } catch (e) {
      console.error('Failed saving employee edit:', e);
      setSnackbar({ open: true, message: e.response?.data?.error || e.message || 'Save failed', severity: 'error' });
    }
  };

  const handleResetPassword = async () => {
    if (!editing) return;
    try {
      const res = await axios.post(`${API_BASE}/api/employees/${editing.id}/reset-password`, {}, { withCredentials: true });
      const temp = res.data && res.data.tempPassword;
      if (temp) {
        // show temporary password to admin in a modal so they can copy it
        setTempPassword(temp);
        setTempPasswordFor(`${editing.firstname || ''} ${editing.lastname || ''}`.trim());
        setTempDialogOpen(true);
      } else {
        alert('Password reset succeeded but no temporary password returned');
      }
    } catch (e) {
      console.error('Reset password failed:', e);
      alert(e.response?.data?.error || e.message || 'Reset failed');
    }
  };

  useEffect(() => {
    // If HR/Admin, fetch managed accounts
    if (isManager) {
      fetchEmployees();
      if (isAdmin) {
        fetchApplicantAudit();
        fetchHrLeaveRequests();
        fetchSalaryChangesAudit();
        fetchPayrollAudit();
      }
    }
  }, [user, isManager, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        const [nonHrRes, hrRes, depRes] = await Promise.all([
          axios.get(`${API_BASE}/api/employees`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/employees?scope=hr`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/deployments`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        const nonHr = Array.isArray(nonHrRes.data) ? nonHrRes.data : [];
        const hr = Array.isArray(hrRes.data) ? hrRes.data : [];
        const deps = Array.isArray(depRes?.data?.rows) ? depRes.data.rows : (Array.isArray(depRes?.data) ? depRes.data : []);
        const depMap = new Map();
        deps.forEach((d) => {
          const uid = String(d.user_id || d.employee_id || '');
          if (!uid) return;
          const existing = depMap.get(uid);
          const dTs = new Date(d.start_date || d.created_at || 0).getTime();
          const eTs = existing ? new Date(existing.start_date || existing.created_at || 0).getTime() : -1;
          const isBetter = (!existing) || (!d.end_date && existing.end_date) || (dTs > eTs);
          if (isBetter) depMap.set(uid, d);
        });
        const map = new Map();
        [...hr, ...nonHr].forEach((r) => {
          if (r && r.id != null) {
            const d = depMap.get(String(r.id));
            map.set(String(r.id), {
              ...r,
              branch_name: d?.branch_name || null,
              branch_code: d?.branch_code || null,
              branch_city: d?.branch_city || null,
              branch_province: d?.branch_province || null,
            });
          }
        });
        setEmployees(Array.from(map.values()));
      } else {
        const res = await axios.get(`${API_BASE}/api/employees`, { withCredentials: true });
        setEmployees(res.data || []);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicantAudit = async () => {
    if (!isAdmin) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/applicants`, { withCredentials: true });
      setApplicants(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setApplicants([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchHrLeaveRequests = async () => {
    if (!isAdmin) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/leave-requests`, { withCredentials: true });
      const rows = Array.isArray(res.data) ? res.data : [];
      const pendingOrRecent = rows.filter((r) => ['pending', 'approved', 'rejected'].includes(String(r.status || '').toLowerCase()));
      setHrLeaveRequests(pendingOrRecent);
    } catch (e) {
      setHrLeaveRequests([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const decideHrLeave = async (id, status) => {
    try {
      await axios.put(`${API_BASE}/api/leave-requests/${id}`, { status }, { withCredentials: true });
      setSnackbar({ open: true, message: `HR leave ${status}.`, severity: 'success' });
      await fetchHrLeaveRequests();
    } catch (e) {
      setSnackbar({ open: true, message: e?.response?.data?.error || `Failed to ${status} leave request.`, severity: 'error' });
    }
  };

  const fetchSalaryChangesAudit = async () => {
    if (!isAdmin) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/salary-changes`, { withCredentials: true });
      setSalaryChanges(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSalaryChanges([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchPayrollAudit = async () => {
    if (!isAdmin) return;
    setAuditLoading(true);
    try {
      const q = `month=${encodeURIComponent(payrollAuditMonth)}&year=${encodeURIComponent(payrollAuditYear)}`;
      const res = await axios.get(`${API_BASE}/api/admin/payroll-audit?${q}`, { withCredentials: true });
      setPayrollAuditRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setPayrollAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const getEmploymentStatus = (contractEndDate) => {
    const raw = String(contractEndDate || '').trim();
    if (!raw) return { label: 'Active', color: 'success', variant: 'filled' };
    const end = new Date(raw);
    if (Number.isNaN(end.getTime())) return { label: 'Active', color: 'success', variant: 'filled' };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const diffDays = Math.ceil((endDay - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Expired', color: 'error', variant: 'filled' };
    if (diffDays <= 30) return { label: `Contract ending soon (${diffDays}d)`, color: 'warning', variant: 'filled' };
    return { label: 'Active', color: 'success', variant: 'filled' };
  };

  const getReviewStatusChip = (rawStatus) => {
    const status = String(rawStatus || 'pending').toLowerCase();
    if (status === 'approved') return <Chip size="small" label="Approved" color="success" sx={{ fontWeight: 700 }} />;
    if (status === 'rejected') return <Chip size="small" label="Rejected" color="error" sx={{ fontWeight: 700 }} />;
    return <Chip size="small" label="Pending" color="warning" sx={{ fontWeight: 700 }} />;
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Please log in to see accounts.</Typography>
      </Box>
    );
  }

  if (isManager) {
    const sections = [
      { id: 'hr-accounts', label: 'HR Accounts', icon: <GroupIcon fontSize="small" /> },
      { id: 'admin-payroll-audit', label: 'Payroll Audit', icon: <HistoryIcon fontSize="small" /> },
      { id: 'hr-leave-audit', label: 'HR Leave Review', icon: <FactCheckIcon fontSize="small" /> },
      { id: 'hr-app-audit', label: 'Applicant Audit', icon: <FactCheckIcon fontSize="small" /> },
      { id: 'hr-salary-audit', label: 'Salary Audit', icon: <HistoryIcon fontSize="small" /> },
    ];

    const jumpToSection = (id) => {
      try {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
      setMobileDrawerOpen(false);
    };
    const adminSectionTitleSx = { mt: 3, mb: 1.25, fontWeight: 800, letterSpacing: 0.2 };
    const auditCardSx = {
      overflow: 'hidden',
      borderRadius: 2,
      border: '1px solid #d5dceb',
      boxShadow: '0 8px 18px rgba(17, 24, 39, 0.06)',
      background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)',
    };
    const auditTableHeadCellSx = {
      fontWeight: 800,
      color: '#1f2a44',
      backgroundColor: '#edf2fb',
      borderBottom: '1px solid #d5dceb',
      whiteSpace: 'nowrap',
    };
    const actionButtonSx = { minWidth: 88, fontWeight: 700, letterSpacing: 0.2 };

    const adminDrawer = (
      <Box sx={{ height: '100%', bgcolor: '#2f5eb8', color: '#fff', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontWeight: 800, letterSpacing: 0.2 }}>EZRA GENERAL</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>ADMIN</Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
        <List sx={{ px: 1, pt: 1 }}>
          {sections.map((s) => (
            <ListItemButton
              key={s.id}
              onClick={() => jumpToSection(s.id)}
              sx={{ borderRadius: 1, color: '#fff', mb: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
            >
              <ListItemIcon sx={{ color: '#fff', minWidth: 36 }}>{s.icon}</ListItemIcon>
              <ListItemText primary={s.label} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ mt: 'auto', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36 }}>{String(user?.firstname || 'A').slice(0, 1).toUpperCase()}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                {`${user?.firstname || 'Admin'} ${user?.lastname || ''}`.trim()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                {String(user?.employeeid || user?.employee_no || '00000000').padStart(8, '0')}
              </Typography>
            </Box>
          </Box>
          <Button fullWidth color="error" variant="contained" startIcon={<LogoutIcon />} onClick={onLogout} sx={{ fontWeight: 700 }}>
            LOGOUT
          </Button>
        </Box>
      </Box>
    );

    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#d8dee8' }}>
        {isAdmin && (
          <>
            <Drawer
              variant="temporary"
              open={mobileDrawerOpen}
              onClose={() => setMobileDrawerOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: ADMIN_DRAWER_WIDTH, boxSizing: 'border-box' } }}
            >
              {adminDrawer}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                width: ADMIN_DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': { width: ADMIN_DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' },
              }}
            >
              {adminDrawer}
            </Drawer>
          </>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {isAdmin && (
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#fff', color: '#111', borderBottom: '1px solid #e3e6ee' }}>
              <Toolbar sx={{ minHeight: 68, flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: { xs: 1, md: 0 } }}>
                <IconButton edge="start" sx={{ mr: 1, display: { md: 'none' } }} onClick={() => setMobileDrawerOpen(true)}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                  Admin Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {String(user?.employeeid || user?.employee_no || '00000000').padStart(8, '0')}
                </Typography>
              </Toolbar>
            </AppBar>
          )}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h5" sx={{ mb: 2 }}>{isAdmin ? 'Admin Control Panel' : 'All Accounts'}</Typography>
        {isAdmin && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            HR account management, applicant reviewer audit, and salary change audit.
          </Typography>
        )}
        {loading ? <CircularProgress /> : null}
        {auditLoading ? <CircularProgress size={18} sx={{ ml: 1 }} /> : null}
        {error ? <Typography color="error">{error}</Typography> : null}
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }} id="hr-accounts">
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
            </TableHead>
            <TableBody>
              {employees.map(e => (
                <TableRow key={e.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpenEdit(e)}>
                  <TableCell>{e.id}</TableCell>
                  <TableCell>{e.employeeid || e.username}</TableCell>
                  <TableCell>{`${e.firstname || ''} ${e.lastname || ''}`.trim()}</TableCell>
                  <TableCell>{e.branch_name || '-'}</TableCell>
                  <TableCell>{e.email}</TableCell>
                  <TableCell>{e.phone}</TableCell>
                  <TableCell>{e.role}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {isAdmin && (
          <>
            <Typography variant="h6" sx={adminSectionTitleSx} id="hr-app-audit">Applicant Approval Audit</Typography>
            <TableContainer component={Paper} sx={{ ...auditCardSx, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <TableRow>
                    <TableCell sx={auditTableHeadCellSx}>Applicant</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Status</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Reviewed By</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Reviewed At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicants.length === 0 ? (
                    <TableRow><TableCell colSpan={4} sx={{ py: 2.5, color: 'text.secondary' }}>No applicant audit rows.</TableCell></TableRow>
                  ) : applicants.map((a) => (
                    <TableRow key={`app-${a.id}`} hover>
                      <TableCell>{`${a.firstname || ''} ${a.lastname || ''}`.trim()}</TableCell>
                      <TableCell>{getReviewStatusChip(a.status)}</TableCell>
                      <TableCell>{a.reviewed_by_name || '-'}</TableCell>
                      <TableCell>{a.reviewed_at ? new Date(a.reviewed_at).toLocaleString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={adminSectionTitleSx} id="hr-leave-audit">HR Leave Review</Typography>
            <TableContainer component={Paper} sx={{ ...auditCardSx, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <TableRow>
                    <TableCell sx={auditTableHeadCellSx}>HR Account</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Leave Type</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Start</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>End</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Status</TableCell>
                    <TableCell align="right" sx={auditTableHeadCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hrLeaveRequests.length === 0 ? (
                    <TableRow><TableCell colSpan={6} sx={{ py: 2.5, color: 'text.secondary' }}>No HR leave requests.</TableCell></TableRow>
                  ) : hrLeaveRequests.map((r) => {
                    const status = String(r.status || '').toLowerCase();
                    return (
                      <TableRow key={`hr-leave-${r.id}`} hover>
                        <TableCell>{[r.firstname, r.lastname].filter(Boolean).join(' ') || r.username || '-'}</TableCell>
                        <TableCell>{r.leave_type || '-'}</TableCell>
                        <TableCell>{r.start_date ? new Date(r.start_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>{r.end_date ? new Date(r.end_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>{getReviewStatusChip(status)}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'inline-flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              sx={actionButtonSx}
                              disabled={status !== 'pending'}
                              onClick={() => decideHrLeave(r.id, 'approved')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={actionButtonSx}
                              disabled={status !== 'pending'}
                              onClick={() => decideHrLeave(r.id, 'rejected')}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={adminSectionTitleSx} id="hr-salary-audit">Salary Change Audit</Typography>
            <TableContainer component={Paper} sx={{ ...auditCardSx, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <TableRow>
                    <TableCell sx={auditTableHeadCellSx}>Employee</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Old Salary</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>New Salary</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Changed By</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Changed At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salaryChanges.length === 0 ? (
                    <TableRow><TableCell colSpan={5} sx={{ py: 2.5, color: 'text.secondary' }}>No salary change records.</TableCell></TableRow>
                  ) : salaryChanges.map((s) => (
                    <TableRow key={`sal-${s.id}`} hover>
                      <TableCell>{(s.target_employeeid || '').toString().padStart(8, '0')} {s.target_name ? `- ${s.target_name}` : ''}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{Number(s.old_salary || 0).toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'success.dark' }}>{Number(s.new_salary || 0).toLocaleString()}</TableCell>
                      <TableCell>{s.changed_by_employeeid || s.changed_by_name || s.changed_by || '-'}</TableCell>
                      <TableCell>{s.changed_at ? new Date(s.changed_at).toLocaleString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" sx={adminSectionTitleSx} id="admin-payroll-audit">Payroll Audit (Who Edited)</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  label="Month"
                  value={payrollAuditMonth}
                  onChange={(e) => setPayrollAuditMonth(Number(e.target.value))}
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Year"
                type="number"
                value={payrollAuditYear}
                onChange={(e) => setPayrollAuditYear(Number(e.target.value || now.getFullYear()))}
                sx={{ width: 130 }}
              />
              <Button variant="contained" sx={{ px: 2.5, fontWeight: 700 }} onClick={fetchPayrollAudit}>Apply</Button>
            </Box>
            <TableContainer component={Paper} sx={{ ...auditCardSx, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 980 }}>
                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <TableRow>
                    <TableCell sx={auditTableHeadCellSx}>Employee</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Role</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Month/Year</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Base Salary</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Net Salary</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Absences</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Edited By (HR)</TableCell>
                    <TableCell sx={auditTableHeadCellSx}>Edited At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payrollAuditRows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} sx={{ py: 2.5, color: 'text.secondary' }}>No payroll records for this period.</TableCell></TableRow>
                  ) : payrollAuditRows.map((r) => (
                    <TableRow key={`pay-audit-${r.id}`} hover>
                      <TableCell>
                        {String(r.employeeid || '').padStart(8, '0')} - {[r.firstname, r.lastname].filter(Boolean).join(' ') || '-'}
                      </TableCell>
                      <TableCell>{r.role || '-'}</TableCell>
                      <TableCell>{`${r.month}/${r.year}`}</TableCell>
                      <TableCell>{Number(r.base_salary || 0).toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'primary.dark' }}>{Number(r.net_salary || 0).toLocaleString()}</TableCell>
                      <TableCell>{Number(r.absences || 0)}</TableCell>
                      <TableCell>{r.edited_by_employeeid || r.edited_by_name || '-'}</TableCell>
                      <TableCell>{r.edited_at ? new Date(r.edited_at).toLocaleString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {/* Edit dialog for HR */}
        <Dialog open={!!editing} onClose={handleCloseEdit} maxWidth="md" fullWidth>
          <DialogTitle>Employee Details</DialogTitle>
          <DialogContent>
            {detailLoading ? (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : editing && (
              <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Identity</Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    <TextField label="Full name" value={editing.full_name || `${editing.firstname || ''} ${editing.lastname || ''}`.trim()} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Role" name="role" value={editing.role || ''} onChange={handleEditChange} fullWidth />
                    <TextField label="First name" name="firstname" value={editing.firstname || ''} onChange={handleEditChange} onKeyDown={lettersKeyDown} fullWidth />
                    <TextField label="Last name" name="lastname" value={editing.lastname || ''} onChange={handleEditChange} onKeyDown={lettersKeyDown} fullWidth />
                    <TextField label="Middle name" name="middlename" value={editing.middlename || ''} onChange={handleEditChange} onKeyDown={lettersKeyDown} fullWidth />
                    <TextField label="Employee ID" name="employeeid" value={editing.employeeid || ''} onChange={handleEditChange} onKeyDown={digitsKeyDown} fullWidth />
                    <TextField label="Email" name="email" value={editing.email || ''} onChange={handleEditChange} fullWidth />
                    <TextField label="Phone" name="phone" value={editing.phone || ''} onChange={handleEditChange} onKeyDown={(e) => phoneKeyDown(e, editing.phone)} fullWidth />
                    <TextField label="Gender" name="gender" value={editing.gender || ''} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Civil Status" name="civil_status" value={editing.civil_status || ''} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Address" name="address" value={editing.address || ''} onChange={handleEditChange} fullWidth sx={{ gridColumn: { md: 'span 2' } }} />
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Employment & Payroll</Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    <TextField label="Date Hired" name="date_hired" value={editing.date_hired || ''} onChange={handleEditChange} fullWidth />
                    <TextField label="Contract End Date" name="contract_end_date" value={editing.contract_end_date || ''} onChange={handleEditChange} fullWidth />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, gridColumn: { md: 'span 2' } }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Employment Status:</Typography>
                      {(() => {
                        const status = getEmploymentStatus(editing.contract_end_date);
                        return <Chip size="small" label={status.label} color={status.color} variant={status.variant} />;
                      })()}
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel>Salary Type</InputLabel>
                      <Select
                        name="salaryType"
                        value={editing.salaryType || editing.salary_type || 'monthly'}
                        onChange={handleEditChange}
                        label="Salary Type"
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField label="Base Salary" name="base_salary" value={editing.base_salary ?? editing.baseSalary ?? ''} onChange={(e)=>{ const v=e.target.value; setEditing(prev=>({ ...prev, base_salary: v, baseSalary: v })); }} fullWidth />
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Branch / Location</Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    <TextField label="Branch Code" value={detailDeployment?.branch_code || editing.branch_code || '-'} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Branch Name" value={detailDeployment?.branch_name || editing.branch_name || '-'} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="City" value={detailDeployment?.branch_city || editing.branch_city || '-'} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Province" value={detailDeployment?.branch_province || editing.branch_province || '-'} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Deployment Start" value={detailDeployment?.start_date || '-'} fullWidth InputProps={{ readOnly: true }} />
                    <TextField label="Deployment End" value={detailDeployment?.end_date || 'Active'} fullWidth InputProps={{ readOnly: true }} />
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Government IDs</Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
                    <TextField label="SSS No (optional)" name="sss_no" value={editing.sss_no || ''} onChange={handleEditChange} onKeyDown={digitsKeyDown} fullWidth placeholder="12-3456789-0" error={!!govError('sss_no', editing.sss_no)} helperText={govError('sss_no', editing.sss_no) || 'Format: 12-3456789-0'} inputProps={{ inputMode: 'numeric', maxLength: 12 }} />
                    <TextField label="PhilHealth No (optional)" name="philhealth_no" value={editing.philhealth_no || ''} onChange={handleEditChange} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!govError('philhealth_no', editing.philhealth_no)} helperText={govError('philhealth_no', editing.philhealth_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} />
                    <TextField label="Pag-IBIG No (optional)" name="pagibig_no" value={editing.pagibig_no || ''} onChange={handleEditChange} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!govError('pagibig_no', editing.pagibig_no)} helperText={govError('pagibig_no', editing.pagibig_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} />
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Personal Details</Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Birthdate"
                        value={editing.birthdate ? new Date(editing.birthdate) : null}
                        onChange={(d) => handleBirthdateChange(d)}
                        slotProps={{ textField: { fullWidth: true, helperText: 'Set birthdate to auto-calculate age' } }}
                      />
                    </LocalizationProvider>
                    <TextField
                      fullWidth
                      label="Age"
                      type="number"
                      name="age"
                      value={editing.age || ''}
                      InputProps={{ readOnly: true }}
                      helperText="Auto-calculated from Birthdate"
                    />
                  </Box>
                </Paper>

                {String(editing.role || '').toLowerCase() === 'custodian' && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Custodian Photo Records</Typography>
                    {detailPhotos.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>No photo records found.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {detailPhotos.slice(0, 12).map((p) => (
                          <Box key={p.id} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 0.5 }}>
                            <img
                              src={`${API_BASE}/api/photos_blob/image/${p.id}`}
                              alt={`Custodian ${editing.employeeid || ''}`}
                              style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 4 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Security</Typography>
                  <TextField label="Password (leave blank to keep)" name="password" value={editing.password || ''} onChange={handleEditChange} type="password" fullWidth />
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit}>Cancel</Button>
            <Button color="warning" onClick={handleResetPassword}>Reset Password</Button>
            <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
          </DialogActions>
        </Dialog>
        {/* Temporary password dialog */}
        <Dialog open={tempDialogOpen} onClose={() => setTempDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Temporary password</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Temporary password for <strong>{tempPasswordFor || (editing && `${editing.firstname || ''} ${editing.lastname || ''}`)}</strong>:</Typography>
              <TextField
                value={tempPassword}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { try { navigator.clipboard.writeText(tempPassword); setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' }); } catch (e) { setSnackbar({ open: true, message: 'Could not copy to clipboard', severity: 'error' }); } }}>Copy</Button>
            <Button onClick={() => setTempDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
          </Box>
        </Box>
      </Box>
    );
  }

  // For non-HR users, show their own profile and allow editing via ProfileDialog
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5">My Account</Typography>
      <Card sx={{ mt: 2, maxWidth: 700 }}>
        <CardContent>
          <Typography variant="subtitle1">Name</Typography>
          <Typography variant="body1">{(user.firstname || user.lastname) ? `${user.firstname || ''} ${user.lastname || ''}`.trim() : user.username}</Typography>
          <Typography variant="subtitle1" sx={{ mt: 2 }}>Email</Typography>
          <Typography variant="body1">{user.email || ''}</Typography>
          <Typography variant="subtitle1" sx={{ mt: 2 }}>Phone</Typography>
          <Typography variant="body1">{user.phone || ''}</Typography>
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setOpenProfile(true)}>Edit Profile</Button>
          </Box>
        </CardContent>
      </Card>

      <ProfileDialog open={openProfile} onClose={() => setOpenProfile(false)} />
    </Box>
  );
}
